import React from "react";
import { formatDate } from "../../utils/dateUtils";
import ActionMenu from "../UsersView/ActionMenu";
/**
 * Extracts cell value from user object based on column key
 * @param {Object} user - User object
 * @param {string} columnKey - Column key identifier
 * @returns {string} Extracted value
 */
export function extractCellValue(sub, columnKey) {
  switch (columnKey) {
    case "userEmail": {
      return sub.userEmail || "—";
    }
    case "userName": {
      return sub.userName || "—";
    }
    case "userId": {
      return sub.userId || "—";
    }
    case "transactionId": {
      return sub.transactionId || "—";
    }
    case "startDate": {
      return sub.startDate ? formatDate(sub.startDate) : "—";
    }
    case "lastUpdated": {
      return sub.lastUpdated ? formatDate(sub.lastUpdated) : "—";
    }
    case "endDate": {
      return sub.endDate ? formatDate(sub.endDate) : "—";
    }
    case "productId": {
      return sub.productId || "—";
    }
    case "plan": {
      return sub.plan || "—";
    }
    case "status": {
      return sub.status || "—";
    }
    case "bootcampUser": {
      return sub.bootcampUser !== null && sub.bootcampUser !== undefined
        ? sub.bootcampUser
          ? "Yes"
          : "No"
        : "—";
    }
    case "autoRenewal": {
      return sub.autoRenewal !== null && sub.autoRenewal !== undefined
        ? sub.autoRenewal
          ? "Yes"
          : "No"
        : "—";
    }
    case "platform": {
      return sub.platform || "—";
    }
    case "promoCode": {
      return sub.promoCode || "—";
    }
    case "amount": {
      return sub.amount !== null && sub.amount !== undefined ? sub.amount : "—";
    }
    case "freeTrialUsed": {
      return sub.freeTrialUsed !== null && sub.freeTrialUsed !== undefined
        ? sub.freeTrialUsed
          ? "Yes"
          : "No"
        : "—";
    }
    case "subLabel": {
      return sub.subLabel || "—";
    }
    case "score": {
      return sub.score !== null && sub.score !== undefined ? sub.score : "—";
    }
    case "secondarySubType": {
      return sub.secondarySubType || "—"; //from users table
    }
    default:
      return "—";
  }
}
/**
 * Creates column definitions for the admin search table
 * @returns {Object} Column definitions object
 */
export function createSubscriptionsColumnDefinitions() {
  const renderHeader = (label) => <th className="px-2 py-3">{label}</th>;

  return {
    actions: {
      key: "actions",
      label: "Actions",
      renderHeader: () => <th className="px-0.5 py-3 text-center"></th>,
      renderCell: (sub, handlers) => (
        <td className="px-0.5 py-3 text-center">
          <ActionMenu onEdit={() => handlers?.onEditSubscription?.(sub.id)} />
        </td>
      ),
    },
    userEmail: {
      key: "userEmail",
      label: "User Email",
      renderHeader: () => renderHeader("User Email"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "userEmail")}</td>
      ),
    },
    userName: {
      key: "userName",
      label: "User Name",
      renderHeader: () => renderHeader("User Name"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "userName")}</td>
      ),
    },
    userId: {
      key: "userId",
      label: "User ID",
      renderHeader: () => renderHeader("User ID"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "userId")}</td>
      ),
    },
    transactionId: {
      key: "transactionId",
      label: "Transaction ID",
      renderHeader: () => renderHeader("Transaction ID"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "transactionId")}</td>
      ),
    },
    startDate: {
      key: "startDate",
      label: "Start Date",
      renderHeader: () => renderHeader("Start Date"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "startDate")}</td>
      ),
    },
    lastUpdated: {
      key: "lastUpdated",
      label: "Updated Date",
      renderHeader: () => renderHeader("Updated Date"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "lastUpdated")}</td>
      ),
    },
    endDate: {
      key: "endDate",
      label: "End Date",
      renderHeader: () => renderHeader("End Date"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "endDate")}</td>
      ),
    },
    productId: {
      key: "productId",
      label: "Product ID",
      renderHeader: () => renderHeader("Product ID"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "productId")}</td>
      ),
    },
    plan: {
      key: "plan",
      label: "Plan",
      renderHeader: () => renderHeader("Plan"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "plan")}</td>
      ),
    },
    status: {
      key: "status",
      label: "Status",
      renderHeader: () => renderHeader("Status"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "status")}</td>
      ),
    },
    bootcampUser: {
      key: "bootcampUser",
      label: "Bootcamp User",
      renderHeader: () => renderHeader("Bootcamp User"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "bootcampUser")}</td>
      ),
    },
    autoRenewal: {
      key: "autoRenewal",
      label: "Auto Renewal",
      renderHeader: () => renderHeader("Auto Renewal"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "autoRenewal")}</td>
      ),
    },
    platform: {
      key: "platform",
      label: "Platform",
      renderHeader: () => renderHeader("Platform"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "platform")}</td>
      ),
    },
    promoCode: {
      key: "promoCode",
      label: "Promo Code",
      renderHeader: () => renderHeader("Promo Code"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "promoCode")}</td>
      ),
    },
    amount: {
      key: "amount",
      label: "Amount",
      renderHeader: () => renderHeader("Amount"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "amount")}</td>
      ),
    },
    freeTrialUsed: {
      key: "freeTrialUsed",
      label: "Free Trial Used",
      renderHeader: () => renderHeader("Free Trial Used"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "freeTrialUsed")}</td>
      ),
    },
    subLabel: {
      key: "subLabel",
      label: "Subscription Label",
      renderHeader: () => renderHeader("Subscription Label"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "subLabel")}</td>
      ),
    },
    score: {
      key: "score",
      label: "Score",
      renderHeader: () => renderHeader("Score"),
      renderCell: (sub) => (
        <td className="px-2 py-3">{extractCellValue(sub, "score")}</td>
      ),
    },
    secondarySubType: {
      key: "secondarySubType",
      label: "Secondary Sub Type",
      renderHeader: () => renderHeader("Secondary Sub Type"),
      renderCell: (sub) => (
        <td className="px-2 py-3">
          {extractCellValue(sub, "secondarySubType")}
        </td>
      ),
    },
  };
}
